// Parsing for the monthly PCFS Loan Manager extract.
//
// Split out of ImportPage so it can be tested against the real workbook rather than
// only exercised by hand through the browser. The page keeps the upload/preview UI; the
// rules for turning a file into LoanImportRows live here.
//
// The contract is the 16 columns of the "Raw Data from LM" sheet, per the workbook's own
// "Column Mapping" tab. All 16 are imported. The mapping sheet also asks for an Early
// Default flag calculated as Note Date + 18 months, which no column supplies and which is
// not built — the comparison date it means is unresolved.

import * as XLSX from 'xlsx';
import type { LoanImportRow } from '@app/shared';

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

// Tiny CSV parser. Handles quoted fields with embedded commas, doubled quotes,
// CRLF endings. Not a full RFC-4180 implementation (no embedded newlines in
// quoted fields), but enough for a save-as-CSV fallback.
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  for (const line of text.replace(/\r\n/g, '\n').split('\n')) {
    if (!line.trim()) continue;
    const fields: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { cur += ch; }
      } else {
        if (ch === ',') { fields.push(cur); cur = ''; }
        else if (ch === '"') { inQuotes = true; }
        else { cur += ch; }
      }
    }
    fields.push(cur);
    rows.push(fields.map(f => f.trim()));
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Workbook
// ---------------------------------------------------------------------------

// Excel's day 0. Loan Manager writes a literal 0 where it has no date, so a zero-valued
// date cell arrives as this instant -- displayed by Excel as "0:00" or "12:00:00 AM",
// which is midnight of a day that does not exist rather than a real date. Anything at or
// before it is Loan Manager's null.
const EXCEL_EPOCH_YEAR = 1900;

// Read a workbook into rows of strings, taking dates from the underlying serial number
// rather than the cell's displayed text.
//
// This matters more than it looks. The extract formats dates as `m/d/yy`, so reading the
// displayed text hands `new Date()` a two-digit year, and its pivot puts 00-49 in the
// 2000s and 50-99 in the 1900s. A 25-year loan disbursed in 2025 matures in 2050 and would
// have imported as **1950** -- a silent hundred-year error in a stored date, on exactly the
// column (Maturity Date) where long horizons are normal. Reading the serial removes both
// the two-digit ambiguity and any dependence on the machine's locale.
export function sheetToRows(sheet: XLSX.WorkSheet): string[][] {
  // raw:true keeps numbers as numbers; cellDates:true (set at read time) turns
  // date-formatted cells into real Dates, which is what lets us bypass the display text.
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: true, defval: '' });
  return rows.map(r => r.map(cellToString));
}

export function readWorkbook(data: ArrayBuffer | Uint8Array): string[][] {
  const wb = XLSX.read(data, { type: 'array', cellDates: true });
  return sheetToRows(wb.Sheets[wb.SheetNames[0]]);
}

function cellToString(cell: unknown): string {
  if (cell == null) return '';
  if (cell instanceof Date) {
    // Excel dates carry no timezone. cellDates builds them in local time, so read them
    // back with local getters -- toISOString() would shift the day in any zone east of
    // UTC and quietly move every imported date back by one.
    const y = cell.getFullYear();
    if (y <= EXCEL_EPOCH_YEAR) return '';   // serial 0 / 1: "no date"
    const m = String(cell.getMonth() + 1).padStart(2, '0');
    const d = String(cell.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;               // ISO, so parseDate takes its exact-match path
  }
  return String(cell);
}

// ---------------------------------------------------------------------------
// Header mapping
// ---------------------------------------------------------------------------

// Map a Loan Manager header label to the canonical LoanImportRow field.
// Accepts the real export labels plus a few likely variants.
export function headerToField(h: string): keyof LoanImportRow | null {
  const k = h.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (['loannbr', 'umwsbloannumber', 'umwsbloannbr', 'umwsbloan', 'loannumber', 'loannum'].includes(k)) return 'umwsbLoanNumber';
  if (['sbanumber', 'sban', 'sbaloannumber', 'sbaloan'].includes(k)) return 'sbaLoanNumber';
  if (['shortname', 'borrowername', 'borrower', 'name'].includes(k)) return 'borrowerName';
  if (['recordtypecode', 'recordtype', 'loantype'].includes(k)) return 'loanType';
  if (['loanamt', 'loanamount', 'approvalgross', 'originalloanbalance'].includes(k)) return 'approvalGross';
  if (['partbalbor', 'partbal', 'guarantybalance'].includes(k)) return 'guarantyBalance';
  if (['sbagrty', 'sbagrtypct', 'sbaguaranty', 'guarantypct', 'gtypct'].includes(k)) return 'guarantyPct';
  if (['notedate'].includes(k)) return 'noteDate';
  if (['maturitydate'].includes(k)) return 'maturityDate';
  if (['naicscode', 'naics'].includes(k)) return 'naicsCode';
  // Stored raw and used to derive soldLoan; never displayed. See migration 030.
  if (['partid', 'participant', 'participantname'].includes(k)) return 'partId';
  if (['sitestate', 'state', 'statelocated'].includes(k)) return 'stateLocated';
  if (['dateinliquid', 'dateinliquidation', 'datainliquid'].includes(k)) return 'dateInLiquidation';
  if (['repurchasedate', 'purchasedate', 'purchaseddate', 'datepurchased'].includes(k)) return 'purchaseDate';
  if (['user18d', 'uncollectibledate', 'sbauncollectibledate', 'datesbauncollectible'].includes(k)) return 'dateSbaUncollectible';
  if (['user20d', 'sbacodate', 'sbachargeoffdate', 'datefinalsbachargeoff'].includes(k)) return 'dateFinalSbaChargeOff';
  return null;
}

// Columns in the extract that map to nothing on purpose, so an unrecognised header can be
// reported as genuinely unrecognised. Empty for now: `Part Id` used to be here and is
// imported as of migration 030.
export const KNOWN_UNMAPPED: string[] = [];

export function isKnownUnmapped(h: string): boolean {
  const k = h.toLowerCase().replace(/[^a-z0-9]/g, '');
  return KNOWN_UNMAPPED.some(u => u.toLowerCase().replace(/[^a-z0-9]/g, '') === k);
}

// ---------------------------------------------------------------------------
// Cell parsing
// ---------------------------------------------------------------------------

export const DATE_FIELDS = new Set<keyof LoanImportRow>([
  'noteDate', 'maturityDate', 'dateInLiquidation', 'purchaseDate',
  'dateSbaUncollectible', 'dateFinalSbaChargeOff',
]);
export const NUMBER_FIELDS = new Set<keyof LoanImportRow>(['approvalGross', 'guarantyBalance', 'guarantyPct']);

// Loan Manager's renderings of "no date". A bare 0 is what the CSV export writes; the
// others are how Excel displays serial 0 under a date or time format, which is what lands
// here when the extract is pasted as text rather than uploaded as a workbook.
const NO_DATE = /^(0|0+:0+(:0+)?|12:00(:00)?\s*am|1\/0\/(00|1900))$/i;

export function parseDate(s: string): string | null {
  if (!s) return null;
  const t = s.trim();
  if (!t || NO_DATE.test(t)) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
  const d = new Date(t);
  if (isNaN(d.getTime())) return null;
  // Excel's epoch reached us as text rather than a serial. Not a date either way.
  if (d.getFullYear() <= EXCEL_EPOCH_YEAR) return null;
  return d.toISOString().slice(0, 10);
}

export function parseNumber(s: string): number | null {
  if (!s) return null;
  let cleaned = s.replace(/[$,\s]/g, '');
  // Accounting-format negatives: (1,234.56) -> -1234.56
  if (/^\(.*\)$/.test(cleaned)) cleaned = '-' + cleaned.slice(1, -1);
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return isNaN(n) ? null : n;
}

// ---------------------------------------------------------------------------
// Rows -> preview
// ---------------------------------------------------------------------------

export type Preview =
  | { error: string; headers?: string[] }
  | { rows: LoanImportRow[]; headers: string[]; unmapped: string[]; dropped: number };

// Shared pipeline: turn a header row + data rows into validated LoanImportRows.
export function rowsToPreview(rows: string[][]): Preview | null {
  if (rows.length === 0) return null;
  if (rows.length < 2) return { error: 'Need at least a header row + one data row.' };
  const [headers, ...dataRows] = rows;
  const mapping = headers.map(h => headerToField(h));
  const required: (keyof LoanImportRow)[] = ['umwsbLoanNumber', 'borrowerName'];
  const missing = required.filter(f => !mapping.includes(f));
  if (missing.length > 0) {
    return {
      error: `Missing required columns: ${missing.join(', ')}. Headers found: ${headers.join(', ')}`,
      headers,
    };
  }
  const parsed: LoanImportRow[] = dataRows.map(cells => {
    const out: LoanImportRow = { umwsbLoanNumber: '', borrowerName: '' };
    mapping.forEach((field, i) => {
      if (!field) return;
      const raw = String(cells[i] ?? '').trim();
      if (field === 'umwsbLoanNumber') out.umwsbLoanNumber = raw;
      else if (field === 'borrowerName') out.borrowerName = raw;
      else if (DATE_FIELDS.has(field)) (out[field] as string | null) = parseDate(raw);
      else if (NUMBER_FIELDS.has(field)) (out[field] as number | null) = parseNumber(raw);
      else (out[field] as string | null) = raw || null;
    });
    return out;
  });
  // A row without both required fields cannot be imported. Trailing blank rows are the
  // usual cause: the extract's sheet declares a range far larger than its data, so a
  // 3-row file arrives as thousands of empty ones. Counting them separately keeps a
  // genuinely malformed row from hiding inside that number.
  const rowsOut = parsed.filter(r => r.umwsbLoanNumber && r.borrowerName);
  const blank = parsed.filter(r => !r.umwsbLoanNumber && !r.borrowerName).length;
  return {
    rows: rowsOut,
    headers,
    // Headers we neither map nor knowingly ignore — a column the extract gained.
    unmapped: headers.filter(h => h && !headerToField(h) && !isKnownUnmapped(h)),
    dropped: parsed.length - rowsOut.length - blank,
  };
}
